import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '@/store/main';
import { ISessionCrew, Session } from '@/types/session.types';
import { PageModule, PageModuleType } from '@/types/module.types';

export const getWorkingProject = (state: RootState) => state.project.workingProject;
export const selectWorkingProject = getWorkingProject;
export const getWorkingProjectUuid = (state: RootState) => state.project.workingProject?.uuid;
export const selectWorkingProjectUuid = getWorkingProjectUuid;
export const getWorkingProjectEventId = (state: RootState) => state.project.workingProject?.event;
export const selectWorkingProjectCreationDate = (state: RootState) => state.project.workingProject?.created_date;
export const getLoadingWorkingProjectError = (state: RootState): string | null =>
  state.project.loadingWorkingProjectError;
export const getIsUpdatingWorkingProject = (state: RootState): boolean => state.project.updatingWorkingProject;
export const getIsLoadingWorkingProject = (state: RootState): boolean => state.project.loadingWorkingProject;
export const getWorkingProjectDesign = (state: RootState) => state.project?.workingProject?.settings?.design;
export const getWorkingProjectSessions = (state: RootState) => state.project?.workingProject?.sessions;
export const selectIsSingleSession = (state: RootState) => state.project?.workingProject?.sessions?.length === 1;
export const selectSingleSessionUuid = (state: RootState) => {
  if (state.project?.workingProject?.sessions?.length !== 1) return;
  return state.project?.workingProject?.sessions?.[0]?.uuid;
};
export const selectIsProjectDuplicating = (state: RootState) => state.project.isProjectDuplicating;
export const selectDuplicateSession = (state: RootState) => state.project.duplicatedSession;
export const selectDuplicatedProject = (state: RootState) => state.project.duplicatedProject;
export const getPublishedStatus = (state: RootState) => state.project?.projectPublishedStatus;
export const selectIsCheckingPublishedStatus = (state: RootState) => state.project.checkingPublishedStatus;
export const selectIsPublishingProject = (state: RootState) => state.project.publishingProject;
export const getIsSettingsUpdating = (state: RootState) => state.project.isSettingsUpdating;
export const getIsDeletingSession = (state: RootState) => state.project.deletingSession;
export const selectIsSavingWorkingProject = (state: RootState) => state.project.savingWorkingProject;
export const selectIsDeletingSession = (state: RootState) => state.project.deletingSession;
export const selectProjectGroups = (state: RootState) => state.project.projectGroups;
export const selectSettingUpPreview = (state: RootState) => state.project.settingUpProjectPreview;
export const selectProjectPreview = (state: RootState) => state.project.workingProject?.event_preview;
export const selectProjectChannel = (state: RootState) => state.project.workingProject?.channel;
export const selectProjectEventId = (state: RootState) => state.project.workingProject?.event;
export const selectActivePreviewPageTab = (state: RootState) => state.project.activePreviewPageTab;
export const selectWorkingProjectType = (state: RootState) => state.project.workingProject?.event_type;

/** @deprecated auto-generated names are handled on the back-end and should not be treated differently */
export const selectProjectIsAutoGeneratedName = (): false => false;
export const selectProjectGlobalTemplateType = (state: RootState) => state.project.workingProject?.global_template_type;
export const selectProjectName = (state: RootState) => state.project.workingProject?.name;
export const selectCreateProjectErrors = (state: RootState) => state.project.createProjectErrors;
export const selectProjectStartTimestamp = (state: RootState) => state.project.workingProject?.start_timestamp;
export const selectProjectEndTimestamp = (state: RootState) => state.project.workingProject?.end_timestamp;
export const selectWorkingProjectDescription = (state: RootState) => state.project.workingProject?.event_description;
export const selectWorkingProjectIsTemplate = (state: RootState) => state.project.workingProject?.is_template;
export const selectWorkingProjectStatus = (state: RootState) => state.project.workingProject?.status;
export const selectProjectCreatedDate = (state: RootState) => state.project.workingProject?.created_date;
export const selectProjectCategories = (state: RootState) => state.project.workingProject?.categories;
export const selectOriginatingTemplateName = (state: RootState) =>
  state.project.workingProject?.originating_template_name || '';
export const selectProjectThemePack = (state: RootState) => state.project.workingProject?.settings?.design?.colors;
export const selectProjectThemePackId = (state: RootState) =>
  state.project.workingProject?.settings?.design?.colors?.color_pack;
export const selectProjectThemeMode = (state: RootState) => state.project.workingProject?.settings?.design?.color_theme;
export const selectProjectSettings = (state: RootState) => state.project.workingProject?.settings;
export const selectSaveId = (state: RootState) => state.project.saveId;
export const selectShowUpdateProjectTypeModal = (state: RootState) => state.project.showUpdateProjectTypeModal;
export const selectProjectIsTest = (state: RootState) => state.project.workingProject?.is_test_event;
export const selectIsLoadingScheduledEmails = (state: RootState) => state.project.loadingScheduledEmails;
export const selectEditOrPublishButtonState = (state: RootState) => state.project.selectEditOrPublishButtonState;
export const selectShowUneditedContent = (state: RootState) => state.project.showUneditedContent;
export const selectShouldDisplayEditProjectThemeTray = (state: RootState) => state.project.displayEditProjectThemeTray;
export const selectIsCourseEnabled = (state: RootState) => state.project?.workingProject?.settings?.course_enabled;
export const selectWorkingProjectRegistrationSettings = (state: RootState) =>
  state.project.workingProject?.registration_settings;
export const selectWorkingProjectMagicLinkRegistrationsPasscodeListId = (state: RootState) =>
  state.project.workingProject?.registration_settings?.magic_links_registration_passcode_list;
export const selectIsDuplicatingSession = (state: RootState) => state.project.duplicatingSession;
export const selectWorkingProjectThemePackModifiedAt = (state: RootState) => {
  return state.project.workingProject?.settings?.design?.theme_pack_modified_at;
};
export const selectIsCreatingNewSession = (state: RootState) => state.project.isCreatingNewSession;
export const selectWorkingProjectLanguages = (state: RootState) => {
  return state.project?.workingProject?.settings?.i18n?.event_languages;
};
export const selectWorkingProjectHomepageLanguages = (state: RootState) => {
  return state.project?.workingProject?.homepage?.languages;
};
export const selectHomepageAndi18nLanguages = createSelector(
  [selectWorkingProjectLanguages, selectWorkingProjectHomepageLanguages],
  (projectLaangs, i18nLangs): string[] => {
    return Array.from(new Set([...(projectLaangs || []), ...(i18nLangs || [])]));
  }
);
export const selectWorkingProjecti18nBaseLanguage = (state: RootState) => {
  return state.project?.workingProject?.settings?.i18n?.base;
};
export const selectWorkingProjectHomepageLanguage = (state: RootState) => {
  return state.project?.workingProject?.homepage?.languages?.[0];
};
export const selectFirstSessionLanguage = (state: RootState) => {
  return (
    state.project?.workingProject?.sessions?.[0]?.default_language ||
    state.project?.workingProject?.sessionsPreviews?.[0]?.languages?.[0]
  );
};
export const selectAllSessionLanguages = createSelector(
  [(state: RootState) => state.project.workingProject?.sessions],
  (sessions): string[] => {
    const langs: string[][] = (sessions || []).map((s) => s.languages);
    return langs.flat();
  }
);
export const selectAllProjectAndSessionLanguages = createSelector(
  [selectHomepageAndi18nLanguages, selectAllSessionLanguages],
  (projectLangs, sessionLangs): string[] => {
    return Array.from(new Set([...projectLangs, ...sessionLangs]));
  }
);
export const selectWorkingProjectImage = (state: RootState) => state.project?.workingProject?.event_image;
export const selectWorkingProjectHomepage = (state: RootState) => state.project?.workingProject?.homepage;
export const selectMainEventBanner = (state: RootState) =>
  state.project?.workingProject?.homepage?.post_register_home_modules.find(
    (module) => module.type === PageModuleType.main_event_banner
  ) ||
  state.project?.workingProject?.homepage?.modules.find((module) => module.type === PageModuleType.main_event_banner);
export const selectHasSession =
  (sessionUuid?: string) =>
  (state: RootState): boolean => {
    if (!sessionUuid) return false;
    return !!state.project?.workingProject?.sessions?.find((s) => s.uuid === sessionUuid);
  };
export const selectSessionByUuid =
  (sessionUuid?: string) =>
  (state: RootState): Session | undefined => {
    if (!sessionUuid) return;
    return state.project?.workingProject?.sessions?.find((s) => s.uuid === sessionUuid);
  };

export const selectFirstSessionId = (state: RootState): number | undefined => {
  return state.project?.workingProject?.sessions?.[0]?.session;
};
export const selectFirstSessionUuid = (state: RootState): string | undefined => {
  return state.project?.workingProject?.sessions?.[0]?.uuid;
};
export const selectSessionIdFromSessionUuid =
  (sessionUuid?: string) =>
  (state: RootState): number | undefined => {
    if (!sessionUuid) return;
    return state.project?.workingProject?.sessions?.find((s) => s.uuid === sessionUuid)?.session;
  };
// createSelector "memoizes" the call to avoid issues with changing return value re-renders
export const selectAllSessionUuids = createSelector([getWorkingProjectSessions], (sessions) => {
  return sessions?.map((s) => s.uuid);
});
/* 
  Note about `selectWorkingProjectDefaultLanguage`: we do have a `getDefaultLanguage` method,
  but that requires selecting the entire project to pass into it which causes unecessary re-renders.
  This selector returns the same value, but with greater specificity, so it won't unecessarily re-render components.
*/
export const selectWorkingProjectDefaultLanguage = (state: RootState) => {
  return (
    selectWorkingProjecti18nBaseLanguage(state) ||
    selectWorkingProjectHomepageLanguage(state) ||
    selectFirstSessionLanguage(state)
  );
};

const engagementPageModuleTypes = [
  PageModuleType.comment_box_prompt,
  PageModuleType.questions,
  PageModuleType.quizzes,
  PageModuleType.survey,
];

export const selectSessionEngagementModules = (sessionUuid?: string) => {
  return createSelector([getWorkingProjectSessions], (sessions): PageModule[] | undefined => {
    if (!sessionUuid) return;
    const session = sessions?.find((s) => s.uuid === sessionUuid);
    const engagementModules = session?.modules.filter(
      (m) => engagementPageModuleTypes.includes(m.type) && m.modules?.length
    );
    return engagementModules;
  });
};

export const selectSessionEngagementTotals = (state: RootState) => state.project.sessionEngagementTotals;

// preview template (/choose/preview-templates/:uuid) selectors
export const getIsLoadingPreviewTemplate = (state: RootState): boolean => state.project.loadingPreviewTemplate;
export const selectPreviewTemplate = (state: RootState) => state.project.previewTemplate;
export const selectPreviewTemplateUuid = (state: RootState) => state.project.previewTemplate?.uuid;
export const selectPreviewTemplateChannel = (state: RootState) => state.project.previewTemplate?.channel;
export const selectPreviewTemplateType = (state: RootState) => state.project.previewTemplate?.event_type;
export const selectPreviewTemplateIsAutoGeneratedName = (state: RootState) =>
  state.project.previewTemplate?.settings?.is_auto_generated_name;
export const selectPreviewTemplateGlobalTemplateType = (state: RootState) =>
  state.project.previewTemplate?.global_template_type;
export const selectPreviewTemplateName = (state: RootState) => state.project.previewTemplate?.name;
export const selectPreviewTemplateThemePack = (state: RootState) =>
  state.project.previewTemplate?.settings?.design?.colors;
export const selectPreviewTemplateThemeMode = (state: RootState) =>
  state.project.previewTemplate?.settings?.design?.color_theme;
export const selectPreviewTemplateSettings = (state: RootState) => state.project.previewTemplate?.settings;
export const selectDisplayPreviewTemplateThemeTray = (state: RootState) =>
  state.project.displayPreviewTemplateThemeTray;
export const selectPreviewTemplateOverrides = (state: RootState) => state.project.previewTemplateOverrides;
export const selectUpdatingProjectPreviewPassword = (state: RootState) => state.project.updatingProjectPreviewPassword;
export const selectWorkingSession = (state: RootState) => state.project.workingSession;
export const selectIsLoadingWorkingSession = (state: RootState) => state.project.loadingWorkingSession;
export const selectSeriesRequired = (state: RootState) => state.project.workingProject?.settings.useSeries;

// Streaming options
export const selectWorkingSessionCaptionsEnabled = (state: RootState) =>
  state.project.workingSession?.streaming_options?.auto_transcripts || false;
export const selectIsDrmRequired = (state: RootState) =>
  state.project.workingProject?.sessions?.[0]?.streaming_options?.drm_required;

export const selectShowAnnouncementsModal = (state: RootState) => state.project.showAnnouncementsModal;

// Activity Log
export const getMostRecentActivityLogUpdate = (state: RootState) => state.project.mostRecentActivityLogUpdate;
export const getMostRecentActivityLogType = (state: RootState) => state.project.mostRecentActivityLogType;

export const selectCrewListPerSession = createSelector(
  [getWorkingProjectSessions],
  (sessions): Record<string, ISessionCrew[]> => {
    return Object.fromEntries((sessions || []).map((session) => [session.uuid, session.crew || []]) || []);
  }
);

export const selectWorkingProjectRegistrationEnabled = (state: RootState) =>
  state.project.workingProject?.registration_on;
export const selectWorkingProjectRegistrationQuestions = (state: RootState) =>
  state.project.workingProject?.registration_questions;
export const selectWorkingProjectRegistrationSteps = (state: RootState) =>
  state.project.workingProject?.registration_steps;

export const selectWorkingProjectSocialSettings = (state: RootState) => state.project.workingProject?.social_settings;
export const selectWorkingProjectSettings = (state: RootState) => state.project.workingProject?.settings;

// greenroom meeting
export const selectGreenroomSessionMeetings = (state: RootState) => {
  return state.project.greenroomSessionMeetings;
};
export const selectGreenroomMeeting = (state: RootState) => state.project.greenroomMeeting;
export const selectGreenroomMeetingLoading = (state: RootState) => state.project.greenroomMeetingLoading;
export const selectGreenroomMeetingError = (state: RootState) => state.project.greenroomMeetingError;
export const selectGreenroomMedia = (state: RootState) => state.project.greenroomMedia;
export const selectGreenroomMediaLoading = (state: RootState) => state.project.greenroomMediaLoading;
export const selectGreenroomMediaError = (state: RootState) => state.project.greenroomMediaError;
export const selectGreenroomShots = (state: RootState) => state.project.greenroomShots;
export const selectGreenroomShotsLoading = (state: RootState) => state.project.greenroomShotsLoading;
export const selectGreenroomShotsError = (state: RootState) => state.project.greenroomShotsError;
export const selectGreenroomGraphics = (state: RootState) => state.project.greenroomGraphics;
export const selectGreenroomGraphicsLoading = (state: RootState) => state.project.greenroomGraphicsLoading;
export const selectGreenroomGraphicsError = (state: RootState) => state.project.greenroomGraphicsError;
export const selectPublishStatusKnown = (state: RootState) => state.project.publishStatusKnown;
